import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";

/**
 * 将 Firebase Auth 错误转为用户可读中文（避免直接展示 Firebase: Error (auth/...)）
 */
const getLoginErrorMessage = (err) => {
  const code = err?.code;
  const messages = {
    "auth/invalid-credential":
      "邮箱或密码不正确，请检查后重试。若忘记密码可使用「忘记密码」找回。",
    "auth/wrong-password": "密码错误，请重新输入。",
    "auth/user-not-found": "该邮箱尚未注册，请检查邮箱或联系管理员。",
    "auth/invalid-email": "邮箱格式不正确，请修改后重试。",
    "auth/user-disabled": "该账号已被禁用，请联系管理员。",
    "auth/too-many-requests":
      "登录尝试过于频繁，请稍后再试或使用「忘记密码」重置。",
    "auth/network-request-failed": "网络连接失败，请检查网络后重试。",
    "auth/operation-not-allowed": "当前不允许使用邮箱密码登录，请联系管理员。",
    "auth/invalid-verification-code": "验证码无效或已过期。",
    "auth/invalid-api-key": "客户端配置异常，请联系管理员。",
  };
  if (code && messages[code]) {
    return messages[code];
  }
  if (typeof err?.message === "string" && err.message.includes("auth/")) {
    return "登录失败，请检查邮箱和密码是否正确。";
  }
  return err?.message || "登录失败，请检查您的邮箱和密码。";
};

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && currentUser) {
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, currentUser, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(getLoginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f4f7fb",
        backgroundImage: `
          radial-gradient(circle at 2px 2px, rgba(25, 118, 210, 0.11) 1.5px, transparent 0),
          linear-gradient(180deg, #f7fafd 0%, #eef2f8 100%)
        `,
        backgroundSize: "50px 50px, 100% 100%",
        backgroundPosition: "0 0, 0 0",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(135deg, rgba(25, 118, 210, 0.06) 0%, transparent 50%),
            linear-gradient(45deg, rgba(25, 118, 210, 0.04) 0%, transparent 50%)
          `,
          pointerEvents: "none",
        },
      }}
    >
      <Container
        component="main"
        maxWidth="xs"
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 4,
              width: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              align="center"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              请登录您的账户
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="邮箱地址"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                hiddenLabel
                inputProps={{ "aria-label": "密码" }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "登录"}
              </Button>
              <Box sx={{ textAlign: "right", mt: 1 }}>
                <Link
                  to="/forgot-password"
                  style={{ textDecoration: "none", color: "#1976d2" }}
                >
                  <Typography variant="body2">忘记密码？</Typography>
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}

export default Login;
